// Type-only: erased at runtime, so this module stays a leaf and importing it
// cannot drag in the risk_bands barrel and its import cycle.
import type { ColorMode } from "@/components/layouts/colorModeScript";
import type { AbcdeRiskValue } from "@/features/risk_bands";
import type { ExposureStatus } from "@/types/exposure";

/**
 * Markers sit on the basemap, which flips with the mode, so the dark tones
 * raise lightness and ease saturation while holding the hue — an A must still
 * read as the same green. C, D and E must stay identical to each other: HKMA
 * ("halo" mode) shows all five letters, and its sidebars index this table by
 * letter, so a red that differs per letter would show three reds there while
 * the map shows one.
 */
export const RATING_COLORS: Record<ColorMode, Record<AbcdeRiskValue | "?", string>> = {
  light: { A: "#22C55E", B: "#FBBF24", C: "#DC2626", D: "#DC2626", E: "#DC2626", "?": "#1D4ED8" },
  dark: { A: "#4ADE80", B: "#FBBF24", C: "#EF4444", D: "#EF4444", E: "#EF4444", "?": "#60A5FA" },
};

export const EXPOSURE_COLORS: Record<ColorMode, Record<ExposureStatus | "?", string>> = {
  light: { exposed: "#0084d1", unexposed: "#52525C", "?": "#9CA3AF" },
  dark: { exposed: "#38BDF8", unexposed: "#71717A", "?": "#9CA3AF" },
};

export interface DataColors {
  ratings: Record<AbcdeRiskValue | "?", string>;
  exposure: Record<ExposureStatus | "?", string>;
}

/** Pure, so the report build can pin `light` without a React tree. */
export const getDataColors = (mode: ColorMode): DataColors => ({
  ratings: RATING_COLORS[mode],
  exposure: EXPOSURE_COLORS[mode],
});

export type RiskBandLetter = "A" | "B" | "C" | "?";

/**
 * What a marker *means*, as opposed to what colour it happens to be drawn in.
 * Colour can't be the identity: dark mode gives one band two hex values, and D
 * and E already share C's red. `custom` is a caller-supplied raw fill.
 *
 * This is a paint grouping, not a rating — nothing reads it back as one. D and
 * E ratings still display their own letter everywhere via `RATING_COLORS`.
 */
export type MarkerBand = `risk:${RiskBandLetter}` | `exposure:${ExposureStatus | "?"}` | "custom";

/** D and E collapse onto C so a cluster shows them as one arc, not three identical ones. */
export const toRiskBand = (graded: AbcdeRiskValue | "?" | null | undefined): MarkerBand => {
  if (graded === "D" || graded === "E") return "risk:C";
  if (graded === "A" || graded === "B" || graded === "C") return `risk:${graded}`;

  return "risk:?";
};

export const toExposureBand = (status: ExposureStatus | "?" | null | undefined): MarkerBand =>
  status === "exposed" || status === "unexposed" ? `exposure:${status}` : "exposure:?";

interface BandStyle {
  /** Ordering for cluster arcs and for which band a tie resolves to. */
  rank: number;
  fill: Record<ColorMode, string>;
  /** The cluster's centre chip. Fixed in both modes: data, not chrome. */
  innerFill: string;
  innerTextColor: string;
}

const ratingFill = (key: AbcdeRiskValue | "?"): Record<ColorMode, string> => ({
  light: RATING_COLORS.light[key],
  dark: RATING_COLORS.dark[key],
});

const exposureFill = (key: ExposureStatus | "?"): Record<ColorMode, string> => ({
  light: EXPOSURE_COLORS.light[key],
  dark: EXPOSURE_COLORS.dark[key],
});

export const MARKER_BAND_STYLE: Record<MarkerBand, BandStyle> = {
  "risk:?": { rank: 0, fill: ratingFill("?"), innerFill: "#1D4ED8", innerTextColor: "white" },
  "risk:A": { rank: 1, fill: ratingFill("A"), innerFill: "#86EFAC", innerTextColor: "#14532D" },
  "risk:B": { rank: 2, fill: ratingFill("B"), innerFill: "#FDE68A", innerTextColor: "#78350F" },
  "risk:C": { rank: 3, fill: ratingFill("C"), innerFill: "#F87171", innerTextColor: "#450A0A" },
  "exposure:?": { rank: 0, fill: exposureFill("?"), innerFill: "#D1D5DB", innerTextColor: "#1F2937" },
  "exposure:unexposed": { rank: 1, fill: exposureFill("unexposed"), innerFill: "#A1A1AA", innerTextColor: "#18181B" },
  "exposure:exposed": { rank: 2, fill: exposureFill("exposed"), innerFill: "#7DD3FC", innerTextColor: "#0C4A6E" },
  custom: { rank: 0, fill: { light: "#FFFFFF", dark: "#FFFFFF" }, innerFill: "#FFFFFF", innerTextColor: "#111827" },
};

export const bandStyle = (band: MarkerBand): BandStyle => MARKER_BAND_STYLE[band] ?? MARKER_BAND_STYLE.custom;

export const bandRank = (band: MarkerBand): number => bandStyle(band).rank;

export const bandFillColor = (band: MarkerBand, mode: ColorMode): string => bandStyle(band).fill[mode];
